import { ReactNode, useEffect, useState } from "react";
import { Asset, FontStyleOverride } from "./types";

type ChildType = "text" | "icon" | "iconfallback" | "newline" | "iconlist" | "subtext";

type ChildData = {
  type: ChildType;
};

type TextChildData = ChildData & {
  text: string;
};

type IconChildData = ChildData & {
  imgUrl: string;
  aspect: number;
};

type IconListChildData = ChildData & {
  icons: Array<IconChildData | TextChildData>;
};

type SubtextChildData = ChildData & {
  subtextId: string;
  subtextContent: string;
};

function checkCorrectlyFormedIconString(text: string) {
  let openingCurlyCount = (text.match(/{/g) ?? []).length;
  let closingCurlyCount = (text.match(/}/g) ?? []).length;
  if (openingCurlyCount != closingCurlyCount) {
    return false;
  }
  let openingSquareCount = (text.match(/\[/g) ?? []).length;
  let closingSquareCount = (text.match(/\]/g) ?? []).length;
  if (openingSquareCount != closingSquareCount) {
    return false;
  }
  return true;
}

function prepareChildData(
  text: string,
  assets: { [key: string]: Asset },
): (TextChildData | IconChildData | IconListChildData | SubtextChildData)[] {
  let preparedData = new Array<TextChildData | IconChildData | IconListChildData | SubtextChildData>();
  let iconListChildren = new Array<IconChildData | TextChildData>();
  let textBuild = "";
  let iconListStartIndex = -1;
  let iconDefStartIndex = -1;
  let iconListDefOpen = false;
  let iconDefOpen = false;
  let textBuildActive = false;
  let cleanText = text.replace(/\n/g, "");
  for (let i = 0; i < cleanText.length; i++) {
    if (
      textBuildActive &&
      (cleanText[i] === "{" ||
        cleanText[i] === "[" ||
        cleanText[i] === "]" ||
        cleanText[i] === "}" ||
        (cleanText[i] === "<" && cleanText.substring(i).startsWith('<span data-subtext="')))
    ) {
      //if we encounter a special char while building text, push text build to prepared data and reset
      preparedData.push({ type: "text", text: textBuild });
      textBuild = "";
      textBuildActive = false;
    }
    if (cleanText[i] === "{") {
      //icon list definition start
      iconListStartIndex = i;
      iconListDefOpen = true;
      continue;
    }

    if (cleanText[i] === "[") {
      //icon definition start
      iconDefStartIndex = i;
      iconDefOpen = true;
      continue;
    }

    if (cleanText[i] === "]") {
      iconDefOpen = false;
      let iconName = cleanText.substring(iconDefStartIndex + 1, i);
      if (iconListDefOpen && assets && assets[iconName]) {
        //add icon to currently open list
        iconListChildren.push({ type: "icon", imgUrl: assets[iconName].url, aspect: assets[iconName].aspect });
      } else if (iconListDefOpen && (!assets || !assets[iconName])) {
        //no asset data, add fallback to list
        iconListChildren.push({ type: "iconfallback", text: iconName });
      } else if (assets && assets[iconName]) {
        //add icon directly
        preparedData.push({ type: "icon", imgUrl: assets[iconName].url, aspect: assets[iconName].aspect });
      } else {
        //no asset data, add fallback
        preparedData.push({ type: "iconfallback", text: iconName });
      }
      continue;
    }

    if (cleanText[i] === "}") {
      //end of iconlist
      iconListDefOpen = false;
      preparedData.push({ type: "iconlist", icons: iconListChildren });
      iconListChildren = new Array<IconChildData>();
      continue;
    }

    if (cleanText[i] === "<" && cleanText.substring(i).startsWith('<span data-subtext="')) {
      //subtext span processing
      let spanOpenerEndIndex = cleanText.indexOf('"', i);
      if (spanOpenerEndIndex !== -1) {
        i = spanOpenerEndIndex + 1;
        let subtextIdEndIndex = cleanText.indexOf('"', i);
        if (subtextIdEndIndex !== -1) {
          let subtextId = cleanText.substring(i, subtextIdEndIndex);
          i = subtextIdEndIndex + 2; //skip closing '>' of opening tag as well
          let spanCloserIndex = cleanText.indexOf("</span>", i);
          if (spanCloserIndex !== -1) {
            let subtextContent = cleanText.substring(i, spanCloserIndex);
            preparedData.push({ type: "subtext", subtextId: subtextId, subtextContent: subtextContent });
            i = spanCloserIndex + 6; //move index to end of closing tag
            continue;
          }
        }
      }
    }

    if (!iconDefOpen && !iconListDefOpen) {
      //no special chars and no icon defs open, build text
      textBuildActive = true;
      textBuild += cleanText[i];
    }

    if (i === cleanText.length - 1 && textBuild.length > 0) {
      //end of input text
      preparedData.push({ type: "text", text: textBuild });
    }
  }

  return preparedData;
}

function generateStyledSubtext(
  subtextId: string,
  subtextContent: string,
  deckFontOverrides: FontStyleOverride[],
  cardFontOverrides: FontStyleOverride[],
) {
  let deckOverride = deckFontOverrides.find((override) => override.subtextId === subtextId);
  let cardOverride = cardFontOverrides.find((override) => override.subtextId === subtextId);
  let style = {};
  if (deckOverride) {
    style = {
      fontFamily: deckOverride.fontFamily,
      fontWeight: deckOverride.fontWeight,
      fontStyle: deckOverride.fontStyle,
      fontSize: deckOverride.fontSize,
      color: deckOverride.fontColor,
      lineHeight: deckOverride.lineHeight,
      WebkitTextStrokeWidth: deckOverride.strokeWidth,
      WebkitTextStrokeColor: deckOverride.strokeColor,
      margin: deckOverride.margin,
      textAlign: deckOverride.textAlign,
    };
  }
  if (cardOverride) {
    style = Object.assign({}, style, {
      fontFamily: cardOverride.fontFamily,
      fontWeight: cardOverride.fontWeight,
      fontStyle: cardOverride.fontStyle,
      fontSize: cardOverride.fontSize,
      color: cardOverride.fontColor,
      lineHeight: cardOverride.lineHeight,
      WebkitTextStrokeWidth: cardOverride.strokeWidth,
      WebkitTextStrokeColor: cardOverride.strokeColor,
      margin: cardOverride.margin,
      textAlign: cardOverride.textAlign,
    });
  }

  return <span style={style} dangerouslySetInnerHTML={{ __html: subtextContent }} />;
}

export function substituteIconsInText(
  text: string,
  fontSize: number | string | undefined,
  assets: { [key: string]: Asset },
  fontSizeRules?: { length: number; size: string }[],
  overrideFontSizeFromRules?: boolean,
  iconHeight?: string | number,
  deckFontOverrides?: FontStyleOverride[],
  cardFontOverrides?: FontStyleOverride[],
  iconMargin?: string,
) {
  let children = new Array<ReactNode>();
  let stringCorrect = checkCorrectlyFormedIconString(text);
  if (!stringCorrect) {
    return <span className="iconized-text">Malformed icon variable text.</span>;
  }
  let preparedData = prepareChildData(text, assets);
  let elementCount = 0;
  for (let item of preparedData) {
    switch (item.type) {
      case "icon": {
        let iconData = item as IconChildData;
        children.push(
          <CardIcon
            imgUrl={iconData.imgUrl}
            aspect={iconData.aspect}
            iconHeight={iconHeight ?? fontSize}
            key={"icon-" + elementCount++}
            fontSize={fontSize}
            iconMargin={iconMargin ?? ""}
          />,
        );
        break;
      }
      case "iconlist": {
        let iconListData = item as IconListChildData;
        children.push(
          <div className="card-iconlist">
            {iconListData.icons.map((iconInfo) => {
              if (iconInfo.type == "icon") {
                let currentIconInfo = iconInfo as IconChildData;
                return (
                  <CardIcon
                    imgUrl={currentIconInfo.imgUrl}
                    aspect={currentIconInfo.aspect}
                    iconHeight={iconHeight ?? fontSize}
                    key={"icon-" + elementCount++}
                    fontSize={fontSize}
                    iconMargin={iconMargin ?? ""}
                  />
                );
              } else {
                return (
                  <span key={"text-" + elementCount++} className="icon-fallback">
                    {(iconInfo as TextChildData).text}
                  </span>
                );
              }
            })}
          </div>,
        );
        break;
      }
      case "iconfallback":
        children.push(
          <span key={"text-" + elementCount++} className="icon-fallback">
            {(item as TextChildData).text}
          </span>,
        );
        break;

      case "text": {
        let biggestLength = 0;
        let thisFontSize = fontSize;
        let textData = item as TextChildData;
        if (!overrideFontSizeFromRules) {
          for (let rule of fontSizeRules ?? []) {
            if (textData.text?.length > rule.length && rule.length > biggestLength) {
              thisFontSize = rule.size;
              biggestLength = rule.length;
              break;
            }
          }
        }

        children.push(
          <span
            key={"text-" + elementCount++}
            dangerouslySetInnerHTML={{ __html: textData.text }}
            style={{ fontSize: thisFontSize }}
          ></span>,
        );
        break;
      }

      case "subtext": {
        let subtextData = item as SubtextChildData;
        children.push(
          generateStyledSubtext(
            subtextData.subtextId,
            subtextData.subtextContent,
            deckFontOverrides ?? [],
            cardFontOverrides ?? [],
          ),
        );
        break;
      }
      default:
        break;
    }
  }
  return <span className="iconized-text">{children}</span>;
}

type CardIconProps = {
  iconHeight: string | number | undefined;
  imgUrl: string;
  aspect: number;
  fontSize: string | number | undefined;
  iconMargin: string;
};

function CardIcon({ iconHeight, imgUrl, aspect, fontSize, iconMargin }: CardIconProps) {
  const [calculatedAspect, setCalculatedAspect] = useState<number | null>(null);

  useEffect(() => {
    if (!aspect) {
      const img = new Image();
      img.src = imgUrl;
      img.onload = () => {
        const calculatedAspect = img.width / img.height;
        setCalculatedAspect(calculatedAspect);
      };
    }
  }, [imgUrl, aspect]);

  const parsedIconHeight = parseInt(iconHeight ? iconHeight.toString() : "40") || 40;
  const backgroundImage = imgUrl.startsWith("url(") ? imgUrl : `url(${imgUrl})`;

  const finalAspect = aspect ?? calculatedAspect ?? 1;

  const iconHeightAdjustment = 0.08;
  const adjustedIconHeight = parsedIconHeight * iconHeightAdjustment;

  let style = {
    backgroundImage,
    height: parsedIconHeight,
    width: parsedIconHeight * finalAspect,
    marginBottom: `min((-${adjustedIconHeight}px - ${parsedIconHeight / 2}px + (${fontSize ? fontSize.toString() : "40px"}/2)), 0px)`,
  };

  if (iconMargin) {
    style = Object.assign({}, style, { margin: iconMargin });
  }

  return <div style={style} className="card-icon" />;
}
